import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { logError } from '../lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        // Suppress expected 401 from logged-out probe
        validateStatus: (status) => (status >= 200 && status < 300) || status === 401
      });
      if (data && data.email) {
        setUser(data);
      } else {
        setUser(false);
      }
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Emergent OAuth callback, skip the /me check —
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== 'undefined' && window.location.hash && window.location.hash.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const register = async (email, password, name, role, city, phone) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/register`,
        { email, password, name, role, city, phone },
        { withCredentials: true }
      );
      // After register, the user is NOT auto-logged in — they must verify their email first.
      // We deliberately do NOT call setUser(data) here.
      return {
        success: true,
        requiresVerification: data?.requires_verification === true,
        verificationEmailSent: data?.verification_email_sent === true,
        email
      };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true, user: data };
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Special-case: backend signals email-not-verified with a structured detail object
      if (detail && typeof detail === 'object' && detail.code === 'email_not_verified') {
        return {
          success: false,
          code: 'email_not_verified',
          email: detail.email || email,
          error: detail.message || 'Please verify your email before logging in.'
        };
      }
      const message = formatApiErrorDetail(detail) || error.message;
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(false);
    } catch (error) {
      logError('Logout error:', error);
    }
  };

  return (
    <AuthContext.Provider value={useMemo(() => ({ user, loading, register, login, logout, checkAuth }), [user, loading, checkAuth])}>
      {children}
    </AuthContext.Provider>
  );
};
